import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Dictation for the composer, using the browser's own speech recognition.
 *
 * Only the recogniser is wrapped, not a MediaRecorder: finojan has no audio
 * endpoint, so recording a blob would produce a button that captures sound and
 * then has nowhere to send it. What is here works entirely in the browser, or
 * reports that it cannot and hides itself.
 */
export interface VoiceController {
  isRecording: boolean;
  supported: boolean;
  error: string | null;
  start: () => void;
  stop: () => void;
}

interface BrowserSpeechRecognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: { results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }> }) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
}

type SpeechRecognitionConstructor = new () => BrowserSpeechRecognition;

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

export function useSpeechRecognition(onTranscript: (transcript: string) => void): VoiceController {
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recognitionRef = useRef<BrowserSpeechRecognition | null>(null);
  // Read once per render rather than at module load: in a test environment the
  // constructor is absent and `supported` must simply be false, not throw.
  const Recognition = typeof window === 'undefined'
    ? undefined
    : window.SpeechRecognition ?? window.webkitSpeechRecognition;

  // Abort rather than stop on unmount: `stop` waits for a final result and would
  // keep the microphone open after the page it belonged to is gone.
  useEffect(() => () => recognitionRef.current?.abort(), []);

  const start = useCallback(() => {
    if (!Recognition || isRecording) return;
    setError(null);
    const recognition = new Recognition();
    recognition.lang = 'fa-IR';
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.onresult = (event) => {
      let finalText = '';
      for (let index = 0; index < event.results.length; index += 1) {
        if (event.results[index].isFinal) finalText += event.results[index][0].transcript;
      }
      if (finalText.trim()) onTranscript(finalText.trim());
    };
    recognition.onerror = (event) => {
      setError(event.error === 'not-allowed'
        ? 'اجازهٔ دسترسی به میکروفن داده نشد.'
        : 'تبدیل گفتار به متن کامل نشد.');
      setIsRecording(false);
    };
    recognition.onend = () => setIsRecording(false);
    recognitionRef.current = recognition;
    recognition.start();
    setIsRecording(true);
  }, [Recognition, isRecording, onTranscript]);

  const stop = useCallback(() => recognitionRef.current?.stop(), []);

  return { isRecording, supported: Boolean(Recognition), error, start, stop };
}
